/* eslint-disable max-lines */
// External dependencies
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY, OrderApiData } from "@swiggy-private/payment-types";

// Internal utilities
import { makeDownstreamApiCall, HTTP_METHODS, GlobalHeaderState } from "@swiggy-private/checkout-core";

// Types and interfaces
import { ApiResponse } from "@payment-client/interfaces/Api";
import { GiftDetails } from "@payment-client/interfaces/SwiggyMoney";

// Helper functions
import { isOrderConfirmed } from "@payment-client/helpers/PublicOrderUtils";
import { getMarketplaceBusinessLine } from "@payment-client/utils/Headers";
import { recordNREvent } from "@swiggy-private/checkout-core";

import { transformDashOrderResponse, transformOrderResponse } from "@payment-client/helpers/Transformers";

// interfaces
import { Headers } from "@payment-client/interfaces/Http";
import { DashOrderResponse } from "@payment-client/interfaces/OrderPublic";

// constants
import { DEFAULT_RETRY_RESPONSE, PUBLIC_API_LOG_TABLES, V3_ENDPOINTS } from "@payment-client/constants/Order";

export async function confirmAssureOrder(
    apiHost: string,
    params: { transactionId: string; orderId: string },
): Promise<ApiResponse<OrderApiData>> {
    const headers = GlobalHeaderState.getHeaders();
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers);
    const confirmOrderParams = {
        ...params,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payment_transaction_id: params.transactionId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        order_id: params.orderId,
    };
    const response = await makeDownstreamApiCall<DashOrderResponse>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.NEW_CONFIRM_ASSURE_ORDER,
            bodyParams: confirmOrderParams,
            options: {
                method: HTTP_METHODS.POST,
            },
        },
        true,
    );
    return transformDashOrderResponse(response, marketplaceBusinessLine) as ApiResponse<OrderApiData>;
}

export async function processPaymentConfirmationStatus(
    apiHost: string,
    params: { transactionId: string; orderId: string },
    nrLogTableName: string,
): Promise<{
    paymentFailureResponse?: ApiResponse<OrderApiData>;
}> {
    // Call payment service to confirm transaction
    const headers = GlobalHeaderState.getHeaders();
    const confirmOrderResponse = await makeDownstreamApiCall<OrderApiData>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.CONFIRM_TRANSACTION_URI,
            bodyParams: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                transaction_id: params.transactionId,
            },
            searchParams: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                transaction_id: params.transactionId,
            },
            options: {
                method: HTTP_METHODS.POST,
            },
        },
        true,
    );

    if (confirmOrderResponse.statusCode !== 0) {
        recordNREvent({
            eventType: nrLogTableName,
            statusCode: confirmOrderResponse.statusCode,
            transactionId: params.transactionId,
            orderId: params.orderId,
            ...headers,
        });

        return { paymentFailureResponse: confirmOrderResponse as ApiResponse<OrderApiData> };
    }

    return {};
}

export function processOrderConfirmationStatus(payload: {
    orderId: string;
    headers: Headers;
    nrLogTableName: string;
    orderDetailsResponse: ApiResponse<DashOrderResponse>;
    status?: string;
    marketplaceBusinessLine: string;
}) {
    const { orderId, status, orderDetailsResponse, nrLogTableName, marketplaceBusinessLine, headers } = payload;
    const isConfirmed = isOrderConfirmed(marketplaceBusinessLine, status);

    if (!isConfirmed) {
        recordNREvent({
            eventType: nrLogTableName,
            status: status || "",
            orderId: orderId,
            ...headers,
        });

        // Create retry response matching server-side logic
        const response: ApiResponse<OrderApiData> = {
            ...orderDetailsResponse,
            ...DEFAULT_RETRY_RESPONSE,
        } as ApiResponse<OrderApiData>;

        return response;
    }

    return transformOrderResponse(orderDetailsResponse, marketplaceBusinessLine);
}

export async function getOrderDetails(
    apiHost: string,
    params: {
        orderId: string;
        marketplaceBusinessLine?: string;
        marketplaceCategory?: string;
    },
): Promise<ApiResponse<DashOrderResponse>> {
    const marketplaceBusinessLine = params.marketplaceBusinessLine || MARKETPLACE_BUSINESS_LINE.DASH;
    const marketplaceCategory = params.marketplaceCategory;

    let endpoint = V3_ENDPOINTS.DASH_ORDER_DETAILS_URI;
    let searchParams: { [key: string]: string } | undefined;

    // Handle different business lines and categories - matching server-side logic
    if (
        marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.DASH &&
        marketplaceCategory === MARKETPLACE_CATEGORY.INSTAMART
    ) {
        endpoint = V3_ENDPOINTS.INSTAMART_ORDER_DETAILS_URI;
    } else if (marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.DINEOUT) {
        endpoint = V3_ENDPOINTS.DINEOUT_ORDER_DETAILS_URI;
        searchParams = {
            extended: "true",
        };
    } else if (marketplaceBusinessLine === MARKETPLACE_BUSINESS_LINE.ASSURE) {
        endpoint = V3_ENDPOINTS.ASSURE_ORDER_DETAILS_URI;
    }

    return (await makeDownstreamApiCall<DashOrderResponse>(
        {
            apiHost,
            endpoint: endpoint.replace("{0}", params.orderId),
            options: {
                method: HTTP_METHODS.GET,
            },
            searchParams,
        },
        true,
    )) as ApiResponse<DashOrderResponse>;
}

export async function confirmIMOrderNew(
    apiHost: string,
    params: { transactionId: string; orderId: string },
): Promise<ApiResponse<OrderApiData>> {
    const headers = GlobalHeaderState.getHeaders();
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers);
    const confirmOrderParams = {
        ...params,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payment_transaction_id: params.transactionId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        order_id: params.orderId,
    };
    const response = await makeDownstreamApiCall<DashOrderResponse>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.NEW_CONFIRM_DASH_ORDER,
            bodyParams: confirmOrderParams,
            options: {
                method: HTTP_METHODS.POST,
            },
        },
        true,
    );
    return transformDashOrderResponse(response, marketplaceBusinessLine) as ApiResponse<OrderApiData>;
}

export async function confirmSwiggyMoneyOrder(
    apiHost: string,
    params: { transactionId: string; orderId: string | number; giftDetails?: GiftDetails; [key: string]: unknown },
): Promise<ApiResponse<OrderApiData>> {
    const confirmParams = {
        transactionId: String(params.transactionId),
        orderId: String(params.orderId),
        creditType: params.giftDetails ? "VOUCHER" : "SELFLOAD",
    };

    return (await makeDownstreamApiCall<OrderApiData>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.SWIGGY_MONEY_ORDER_URI,
            bodyParams: confirmParams,
            options: {
                method: HTTP_METHODS.PATCH,
            },
        },
        true,
    )) as ApiResponse<OrderApiData>;
}

export async function confirmEditOrder(
    apiHost: string,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    params: { transaction_id: string; order_id: string; [key: string]: unknown },
): Promise<ApiResponse<OrderApiData>> {
    return (await makeDownstreamApiCall<OrderApiData>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.CONFIRM_EDIT_ORDER_URI,
            bodyParams: params,
            options: {
                method: HTTP_METHODS.POST,
            },
        },
        true,
    )) as ApiResponse<OrderApiData>;
}

export async function confirmOneOrder(apiHost: string, orderId: string): Promise<ApiResponse<OrderApiData>> {
    return (await makeDownstreamApiCall<OrderApiData>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.CONFIRM_ORDER_URI,
            options: {
                method: HTTP_METHODS.POST,
            },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            searchParams: { order_id: orderId },
        },
        true,
    )) as ApiResponse<OrderApiData>;
}

export async function confirmSuperOrder(apiHost: string, orderId: string): Promise<ApiResponse<OrderApiData>> {
    return (await makeDownstreamApiCall<OrderApiData>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.SUPER_CONFIRM_ORDER_URI,
            options: {
                method: HTTP_METHODS.POST,
            },
            // eslint-disable-next-line @typescript-eslint/naming-convention
            searchParams: { order_id: orderId },
        },
        true,
    )) as ApiResponse<OrderApiData>;
}

export async function confirmDineoutOrderNew(
    apiHost: string,
    params: { transactionId: string; orderId: string | number; [key: string]: unknown },
): Promise<ApiResponse<OrderApiData>> {
    const headers = GlobalHeaderState.getHeaders();
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers);
    const confirmOrderParams = {
        ...params,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        payment_transaction_id: params.transactionId,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        order_id: String(params.orderId),
    };

    const confirmOrderResponse = await makeDownstreamApiCall<DashOrderResponse>(
        {
            apiHost,
            endpoint: V3_ENDPOINTS.DINEOUT_CONFIRM_ORDER_URI,
            bodyParams: confirmOrderParams,
            options: {
                method: HTTP_METHODS.POST,
            },
        },
        true,
    );

    recordNREvent({
        eventType: PUBLIC_API_LOG_TABLES.DINEOUT_NEW_CONFIRM_ORDER_STATUS,
        statusCode: confirmOrderResponse.statusCode,
        transactionId: params.transactionId,
        orderId: params.orderId,
        ...headers,
    });

    return transformDashOrderResponse(confirmOrderResponse, marketplaceBusinessLine) as ApiResponse<OrderApiData>;
}
